import { ArrowLeft, Download, ShieldCheck, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { SEO } from "@/components/SEO";
import { APK_URL, androidInstallUrl } from "@/components/landing/appInstall";
import { StepList } from "@/components/landing/StepList";
import { useI18nStore } from "@/stores/i18nStore";

/**
 * Dedicated Android install page at /android-install.
 *
 * It exists because the install instructions have to be reachable from a phone
 * that is not the device the visitor is reading the landing page on: the
 * typical path is "see the site on a laptop, scan a code, finish on the phone".
 * A short URL people can also type is the fallback for a camera that will not
 * scan.
 *
 * The QR here therefore encodes THIS page, not the APK. Scanning it hands the
 * phone the instructions; the download is one tap further on. Encoding the APK
 * directly would start a download on a phone whose owner has not yet been told
 * about the "install unknown apps" prompt they are about to meet.
 *
 * As on the landing page, an unset VITE_ANDROID_APK_URL renders the Chrome
 * install path rather than a download button pointing nowhere.
 */
export function AndroidInstallPage() {
  const { t } = useI18nStore();

  const apkSteps = [
    t.appInstallAndroidStep1,
    t.appInstallAndroidStep2,
    t.appInstallAndroidStep3,
    t.appInstallAndroidStep4,
    t.appInstallAndroidStep5,
  ];

  const pwaSteps = [
    t.appInstallAndroidPwaStep1,
    t.appInstallAndroidPwaStep2,
    t.appInstallAndroidPwaStep3,
    t.appInstallAndroidPwaStep4,
    t.appInstallAndroidPwaStep5,
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Install HealthPass on Android"
        description="Step-by-step instructions for installing HealthPass on an Android phone, by APK download or as an installable web app from Chrome."
        keywords="healthpass android, install healthpass, healthpass apk, android health app"
        url="https://travelhealthpass.app/android-install"
        canonical="https://travelhealthpass.app/android-install"
      />

      {/* pt-28, not py-16: the global OfflineBanner is fixed at top-16 and would
          otherwise cover the heading whenever it is showing. */}
      <div className="bg-gradient-to-r from-green-700 to-blue-600 pb-16 pt-28 text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15">
              <Smartphone className="h-6 w-6" aria-hidden="true" />
            </span>
            <h1 className="text-3xl font-bold md:text-4xl">
              {t.androidInstallPageTitle}
            </h1>
          </div>
          <p className="max-w-2xl text-lg text-blue-50">
            {t.androidInstallPageSubtitle}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Hidden on a phone. This code exists to carry a desktop reader over
            to their handset; once they are on the handset it is a code they
            cannot scan, sitting between them and the download button. Two such
            codes above the button read as "scan something else first". */}
        <div className="mb-8 hidden flex-col items-center gap-6 rounded-2xl border border-gray-200 bg-white p-6 md:flex md:flex-row md:p-8">
          <div
            className="keep-light rounded-xl border-2 border-gray-200 p-3"
            data-testid="android-page-self-qr"
          >
            <QRCode value={androidInstallUrl()} size={128} level="M" />
          </div>
          <p className="flex-1 text-center text-gray-600 md:text-left">
            {t.androidInstallScanPageQr}
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          {APK_URL ? (
            <>
              <div className="mb-8 flex flex-col items-center gap-6 md:flex-row">
                {/* Desktop only, for the same reason as the code above. */}
                <div
                  className="keep-light hidden rounded-xl border-2 border-gray-200 p-3 md:block"
                  data-testid="android-page-apk-qr"
                >
                  <QRCode value={APK_URL} size={144} level="M" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <p className="mb-4 hidden text-gray-600 md:block">
                    {t.appInstallScanToDownload}
                  </p>
                  {/* "download" rather than a plain link: some browsers reached
                      through a QR scanner navigate to the file instead of
                      saving it, and a navigation to an .apk does nothing
                      visible at all. */}
                  <a
                    href={APK_URL}
                    download="HealthPass.apk"
                    className="inline-flex items-center gap-2 rounded-lg bg-green-700 px-6 py-3 font-semibold text-white transition hover:bg-green-800"
                    data-testid="android-page-apk-link"
                  >
                    <Download className="h-5 w-5" aria-hidden="true" />
                    {t.appInstallDownloadApk}
                  </a>
                </div>
              </div>
              <StepList steps={apkSteps} />
            </>
          ) : (
            <>
              <div
                className="mb-8 rounded-xl border border-amber-200 bg-amber-50 p-4"
                data-testid="android-page-apk-unavailable"
              >
                <p className="mb-1 font-semibold text-amber-900">
                  {t.appInstallApkUnavailable}
                </p>
                <p className="text-sm text-amber-800">
                  {t.appInstallApkUnavailableHint}
                </p>
              </div>

              <h2 className="mb-4 text-xl font-semibold text-gray-900">
                {t.appInstallAndroidPwaTitle}
              </h2>
              <StepList steps={pwaSteps} />
            </>
          )}

          {/* Only the APK path meets the unknown-sources prompt. Installing the
              web app from Chrome never asks for that permission, so showing this
              on the Chrome path would warn about something that never happens. */}
          {APK_URL ? (
            <p
              className="mt-8 flex items-start gap-2 rounded-lg bg-blue-50 p-4 text-sm text-blue-900"
              data-testid="android-page-security-note"
            >
              <ShieldCheck
                className="mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <span>{t.androidInstallSecurityNote}</span>
            </p>
          ) : null}
        </div>

        <div className="mt-8 flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/#get-the-app"
            className="font-semibold text-blue-700 hover:underline"
          >
            {t.androidInstallIosHint}
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t.androidInstallBackHome}
          </Link>
        </div>
      </div>
    </div>
  );
}
